// Simone thread pointer — the one piece of chat state that must OUTLIVE the
// page, so a reload does not reset the panel to an empty "New chat".
//
// SIMONE-INFLIGHT-TURN-NO-PERSISTENCE-1. The transcript itself is NOT copied
// here: the conversation is server-owned (sdk.ai.getConversation) and copying
// it into browser storage would fork the source of truth and leave workspace
// content sitting in localStorage. What cannot be re-derived after a reload is
// WHICH thread was open — `root.activeSessionId` is in-memory only, so on the
// next mount the panel had no id to load and showed an empty chat even though
// the server still held the whole conversation. That pointer is what this
// module persists.
//
// It also carries `inflight`: true from the moment a turn is dispatched until
// the turn settles. A pointer restored with `inflight: true` means the page
// went away mid-turn — the approval gate's parked promise
// (simoneAiTransport `_pendingToolApprovals`) died with the page, so any action
// that was waiting for approval never ran. The restore surfaces that instead of
// leaving the user to guess (and retry a write that may already exist).
//
// Pure + store-injected on purpose: no direct `localStorage` reference, so the
// whole policy is unit-testable and a storage-less host (SSR, standalone
// simone without a DOM) degrades to a no-op instead of throwing.

// A pointer older than this is not a reload — it is a new visit. Restoring a
// week-old thread on a fresh open would be a surprise, not a recovery.
export const THREAD_POINTER_TTL_MS = 24 * 60 * 60 * 1000

const PREFIX = 'smbls.simone.thread'

// One pointer per (scope, tenant, agent): the general drawer, the dashboard
// thread and each /add-app extension keep separate threads, exactly as their
// `_ax_`/`_db_` root-key namespaces do. add-app is project-scoped, the others
// workspace-scoped — a pointer must never restore another workspace's
// conversation after an org switch, so the tenant id is part of the key.
//
// `agentId` is the third dimension (AI-TALK-TO-AGENT-REUSES-OPEN-THREAD-1,
// answer C — Nika 2026-08-30): a named workspace agent is a separate
// correspondent, so its history belongs to it and entering from its card
// resumes ITS thread, never whatever was last open. Absent means the general
// assistant, and that key is byte-identical to the pre-answer-C one, so a live
// general thread is not lost to this change.
export const threadPointerKey = (scope, tenantId, agentId) => {
  const s = scope === 'addapp' || scope === 'dashboard' ? scope : 'general'
  const base = `${PREFIX}.${s}.${tenantId || 'none'}`
  return agentId ? `${base}.agent.${agentId}` : base
}

export const readThreadPointer = (store, key, now = Date.now()) => {
  if (!store || !key) return null
  let raw = null
  try {
    raw = store.getItem(key)
  } catch (_) {
    return null
  }
  if (!raw) return null
  let val = null
  try {
    val = JSON.parse(raw)
  } catch (_) {
    // A corrupt entry is not a pointer — drop it rather than throwing on every
    // mount from then on.
    try {
      store.removeItem(key)
    } catch (_) {}
    return null
  }
  if (!val || typeof val !== 'object' || !val.id) return null
  // A local `s-…` placeholder names no server conversation — there is nothing
  // to load it from, so it is not a restorable pointer.
  if (String(val.id).startsWith('s-')) return null
  const ts = Number(val.ts) || 0
  if (!ts || now - ts > THREAD_POINTER_TTL_MS) return null
  return { id: String(val.id), inflight: !!val.inflight, ts }
}

export const writeThreadPointer = (store, key, id, inflight = false, now = Date.now()) => {
  if (!store || !key) return false
  if (!id || String(id).startsWith('s-')) return clearThreadPointer(store, key)
  try {
    store.setItem(key, JSON.stringify({ id: String(id), inflight: !!inflight, ts: now }))
    return true
  } catch (_) {
    // Private mode / quota — persistence is a nicety, never a blocker.
    return false
  }
}

export const clearThreadPointer = (store, key) => {
  if (!store || !key) return false
  try {
    store.removeItem(key)
    return true
  } catch (_) {
    return false
  }
}

// The honest row a restored-mid-turn thread gets. Deliberately narrow: only the
// approval-gated CLIENT action is provably un-run (its promise died with the
// page). A server-side tool may well have completed, so this never claims
// "nothing happened" — that overclaim is what makes a user retry a write that
// already landed.
export const INTERRUPTED_TURN_NOTICE =
  'That answer stopped when the page reloaded. Anything that was waiting for your approval did not run. Check the result before you ask again.'

// Should the restored transcript get the interrupted-turn notice? Only when the
// turn really did die: if the server finished it after the page went away, the
// reloaded thread already ends in an assistant reply and needs no notice.
export const needsInterruptedNotice = (pointer, messages) => {
  if (!pointer || !pointer.inflight) return false
  const list = Array.isArray(messages) ? messages : []
  if (!list.length) return false
  const last = list[list.length - 1]
  if (!last) return false
  if (last.role !== 'assistant') return true
  return !String(last.text || '').trim()
}
